// The 3-up responsive grid of workflow cards. PE/IB bullet counts come from GET /api/landing/stats
// (Redis-cached ~6h on the server). Until that responds — or if it fails — we keep the bundled fallbacks.
import { useQuery } from '@tanstack/react-query';
import {
  FALLBACK_LANDING_STATS,
  WORKFLOW_CARDS,
  ibCardBullets,
  peCardBullets,
  type WorkflowCardSpec,
} from '@/data/workflowCards';
import type { ScoutVertical } from '@/components/layout/Sidebar/sidebarNav';
import { landingService } from '@/services/api';
import WorkflowCard from './WorkflowCard';

export interface WorkflowCardGridProps {
  /** False when the user lacks `pe:dataset` — the PE card renders locked. IB is always coming-soon. */
  canPeDataset: boolean;
  onSelect: (workflow: ScoutVertical) => void;
}

const STATS_STALE_MS = 6 * 60 * 60 * 1000;

function withLiveCounts(spec: WorkflowCardSpec, stats = FALLBACK_LANDING_STATS): WorkflowCardSpec {
  if (spec.workflow === 'pe') return { ...spec, bullets: peCardBullets(stats.pe) };
  if (spec.workflow === 'ib') return { ...spec, bullets: ibCardBullets(stats.ib) };
  return spec;
}

export default function WorkflowCardGrid({ canPeDataset, onSelect }: WorkflowCardGridProps) {
  const { data } = useQuery({
    queryKey: ['landing', 'stats'],
    queryFn: landingService.getStats,
    staleTime: STATS_STALE_MS,
    gcTime: STATS_STALE_MS,
    retry: false,
  });
  const stats = data ?? FALLBACK_LANDING_STATS;

  return (
    <div className="wl-grid">
      {WORKFLOW_CARDS.map((spec) => (
        <WorkflowCard
          key={spec.workflow}
          spec={withLiveCounts(spec, stats)}
          locked={Boolean(spec.comingSoon) || (spec.workflow === 'pe' && !canPeDataset)}
          onSelect={onSelect}
        />
      ))}
    </div>
  );
}
